import { useState, useEffect, useCallback } from 'react';
import { customerApiService, loanApiService } from 'services/api';
import { EmployeeApproval, LoanApproval } from 'types';

export const useApprovals = () => {
  const [employeeApprovals, setEmployeeApprovals] = useState<EmployeeApproval[]>([]);
  const [loanApprovals, setLoanApprovals] = useState<LoanApproval[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEmployeeApprovals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await customerApiService.getPendingPhotoApprovals();
      if (response.success && response.data) {
        setEmployeeApprovals(response.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch employee approvals');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLoanApprovals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // For employers we only provide a read-only view of company loan applications
      // Fetch company loan applications (backend validates company via token)
      const response = await loanApiService.getCompanyLoanApplications();
      if (response && response.success && response.data) {
        // response.data may be a paginated wrapper or an array depending on implementation
        const apps = Array.isArray(response.data) ? response.data : (response.data.data || response.data);
        setLoanApprovals(Array.isArray(apps) ? apps : []);
      } else {
        // On error, default to empty
        setLoanApprovals([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch loan applications');
      setLoanApprovals([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const approveEmployeePhoto = useCallback(async (employeeId: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await customerApiService.approveEmployeePhoto(employeeId);
      if (response.success) {
        await fetchEmployeeApprovals();
        return true;
      }
      return false;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve employee photo');
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchEmployeeApprovals]);

  const rejectEmployeePhoto = useCallback(async (employeeId: string, reason: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await customerApiService.rejectEmployeePhoto(employeeId, reason);
      if (response.success) {
        await fetchEmployeeApprovals();
        return true;
      }
      return false;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject employee photo');
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchEmployeeApprovals]);

  const approveLoan = useCallback(async (
    applicationId: string,
    approvalData: {
      approvedAmount: number;
      interestRate: number;
      term: number;
      notes?: string;
    }
  ) => {
    // Deprecated: employer approvals have been migrated to admins.
    console.warn('approveLoan called: employer approvals are deprecated and handled by admins');
    return false;
  }, [fetchLoanApprovals]);

  const rejectLoan = useCallback(async (applicationId: string, reason: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await loanApiService.rejectLoanApplication(applicationId, reason);
      if (response.success) {
        await fetchLoanApprovals();
        return true;
      }
      return false;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject loan');
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchLoanApprovals]);

  const refreshApprovals = useCallback(async () => {
    await Promise.all([fetchEmployeeApprovals(), fetchLoanApprovals()]);
  }, [fetchEmployeeApprovals, fetchLoanApprovals]);

  useEffect(() => {
    refreshApprovals();
  }, [refreshApprovals]);

  return {
    employeeApprovals,
    loanApprovals,
    loading,
    error,
    approveEmployeePhoto,
    rejectEmployeePhoto,
    approveLoan,
    rejectLoan,
    refreshApprovals,
  };
};

